import React from 'react';
import wishlistIconButtonStyle from './WishlistIconButton.scss';
import {Button, StatesButton, StatesButtonStates} from 'wix-ui-tpa/cssVars';
import {LikeHeart, LikeHeartFill} from '@wix/wix-ui-icons-common/on-stage';
import classNames from 'classnames';
import {ProvidedGlobalProps, withGlobalProps} from '../../../providers/globalPropsProvider';
import {
  IProvidedTranslationProps,
  withTranslations,
} from '@wix/wixstores-client-common-components/dist/es/src/outOfIframes/translations';
import {ActionStatus} from '@wix/wixstores-client-core/dist/es/src/constants';
import {ConditionalRender} from '../../ConditionalRender/ConditionalRender';
import {classes as wishlistButtonStyles} from './WishlistButton.st.css';

export enum Mode {
  INLINE,
  FLOATING,
  WITH_SLOT,
}

export const WishlistButtonDataHook = {
  Root: 'wishlist-button',
  StatesButton: 'wishlist-states-button',
  Icon: 'wishlist-button-icon',
  IconContainer: 'wishlist-button-icon-container',
};

export interface WishlistButtonProps extends ProvidedGlobalProps, IProvidedTranslationProps {
  mode: Mode;
}

class WishlistButtonComponent extends React.Component<WishlistButtonProps> {
  private get isFloatingMode() {
    const {mode} = this.props;

    return mode === Mode.FLOATING;
  }

  private get isWithSlotMode() {
    const {mode} = this.props;

    return mode === Mode.WITH_SLOT;
  }

  private readonly handleWishlistButtonClick = (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    const {handleWishlistButtonClick} = this.props.globals;
    handleWishlistButtonClick();
  };

  private getButtonAriaLabel() {
    const {
      t,
      globals: {productWasAddedToWishlist},
    } = this.props;

    return productWasAddedToWishlist ? t('sr.wishlist.remove.button') : t('sr.wishlist.button');
  }

  private IconButton() {
    const {
      globals: {productWasAddedToWishlist, shouldShowSubscribeButton},
    } = this.props;

    const buttonClasses = classNames('wishlistButton', wishlistIconButtonStyle.wishlistIconButton, {
      [wishlistButtonStyles.wishlistButton]: !this.isFloatingMode,
      [wishlistButtonStyles.wishlistButtonFloatingMode]: this.isFloatingMode,
      [wishlistButtonStyles.wishlistButtonWithSlotMode]: this.isWithSlotMode,
      [wishlistIconButtonStyle.floatingMode]: this.isFloatingMode,
      [wishlistIconButtonStyle.withSubscribeButton]: shouldShowSubscribeButton,
      [wishlistIconButtonStyle.withoutSubscribeButton]: !shouldShowSubscribeButton,
    });

    return (
      <Button
        type="button"
        onClick={this.handleWishlistButtonClick}
        className={buttonClasses}
        data-hook={WishlistButtonDataHook.Root}
        aria-label={this.getButtonAriaLabel()}
        aria-pressed={productWasAddedToWishlist}>
        <i className={wishlistIconButtonStyle.iconContainer} data-hook={WishlistButtonDataHook.IconContainer}>
          {productWasAddedToWishlist ? (
            <LikeHeartFill
              className={classNames(wishlistIconButtonStyle.heartIcon, wishlistIconButtonStyle.filled)}
              data-hook={WishlistButtonDataHook.Icon}
            />
          ) : (
            <LikeHeart
              className={classNames(wishlistIconButtonStyle.heartIcon, wishlistIconButtonStyle.empty)}
              data-hook={WishlistButtonDataHook.Icon}
            />
          )}
        </i>
      </Button>
    );
  }

  private readonly renderIconButton = () => {
    const {
      globals: {shouldShowSubscribeButton},
    } = this.props;

    if (shouldShowSubscribeButton || this.isFloatingMode) {
      return this.IconButton();
    } else {
      return <ConditionalRender by="shouldRenderWishlistIconButton">{this.IconButton()}</ConditionalRender>;
    }
  };

  private FullWidthButton() {
    const {
      t,
      globals: {productWasAddedToWishlist, wishlistActionStatus, resetWishlistStatus},
    } = this.props;

    const caption = productWasAddedToWishlist ? t('wishlist.remove.button') : t('wishlist.button');

    return (
      <StatesButton
        state={wishlistActionStatus === ActionStatus.SUCCESSFUL ? StatesButtonStates.SUCCESS : StatesButtonStates.IDLE}
        idleContent={caption}
        onNotificationEnd={() => resetWishlistStatus()}
        onClick={this.handleWishlistButtonClick}
        className={wishlistButtonStyles.wishlistFullWidthButton}
        disabled={false}
        fullWidth
        data-hook={WishlistButtonDataHook.StatesButton}
      />
    );
  }

  private readonly renderFullWidthButton = () => {
    const {
      globals: {shouldShowSubscribeButton},
    } = this.props;

    if (!shouldShowSubscribeButton && !this.isFloatingMode) {
      return <ConditionalRender by="shouldRenderWishlistFullWidthButton">{this.FullWidthButton()}</ConditionalRender>;
    }
  };

  public render() {
    return (
      <>
        {this.renderIconButton()}
        {this.renderFullWidthButton()}
      </>
    );
  }
}

export const WishlistButton = withGlobalProps(withTranslations('globals.texts')(WishlistButtonComponent));
